import { useEffect, useState } from 'react'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Box, Button, IconButton, Link, useMediaQuery, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useAppKit } from '@reown/appkit/react'
import { useAccount } from 'wagmi'

import { TextInput } from 'src/components/text-input'
import { useCopy } from 'src/hooks'
import { getPolygonscanAddressUrl, handleShowWalletAddress } from 'src/common/utils'

import { useStyles } from './styles'
import { LinkIcon } from 'src/assets/icons'
import { ETranslationNs, IProfileUserForm, IUserWalletBase } from 'src/types'

import { UserWalletsDetailsModal } from './user-wallets-details-modal.component'
import { useFormikContext } from 'formik'
import { HelperWrapper } from '../helper-wrapper'

interface Props {
  fullWidth?: boolean
  withLink?: boolean
}

export const UserWalletInput = ({ fullWidth, withLink }: Props) => {
  const [t] = useTranslation(ETranslationNs.Form)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const classes = useStyles()
  const theme = useTheme()

  const { values, setFieldValue } = useFormikContext<IProfileUserForm>()

  const activeWallet = values.wallets.find((wallet) => wallet.active)

  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))
  const isSTablet = useMediaQuery(theme.breakpoints.down('tablet_s'))
  const isLMobile = useMediaQuery(theme.breakpoints.down('mobile_l'))
  const isMMobile = useMediaQuery(theme.breakpoints.down('mobile_m'))
  const isSMobile = useMediaQuery(theme.breakpoints.down('mobile_s'))

  const handleCopy = useCopy(activeWallet?.address)

  const { open } = useAppKit()
  const { address: connectedAddress } = useAccount()

  const handleDefaultWalletChange = (selectedAddress: string) => {
    const normalizedAddress = selectedAddress.toLowerCase()

    // Check if the wallet already exists in the array
    const walletExists = values.wallets.some(
      (wallet) => wallet.address.toLowerCase() === normalizedAddress,
    )

    const updatedWallets = values.wallets.map((wallet) => ({
      ...wallet,
      active: wallet.address.toLowerCase() === normalizedAddress,
    }))

    // If the wallet is not yet in the array, add a new wallet entry.
    if (!walletExists) {
      const newWallet = {
        address: normalizedAddress,
        is_custodial: false,
        active: true,
        scans: 0,
        eth_balance: 0,
        trai_balance: 0,
      }

      updatedWallets.push(newWallet)
    }

    setFieldValue('wallets', updatedWallets)
  }

  let label = ''
  if (activeWallet && activeWallet.id) {
    if (activeWallet.is_custodial) {
      label = `(${t('user-wallet-custodial-type-label.v')?.toLowerCase()})`
    } else {
      label = `(${t('user-wallet-self-managed-type-label.v')?.toLowerCase()})`
    }
  } else {
    label = `(${t('user-wallet-not-saved-type-label.v')?.toLowerCase()})`
  }

  const handleUpdateClick = () => {
    open()
  }

  const handleClose = () => {
    setIsModalOpen(false)
  }

  useEffect(() => {
    if (connectedAddress && connectedAddress !== activeWallet?.address) {
      handleDefaultWalletChange(connectedAddress.toLowerCase())
    }
  }, [connectedAddress])

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', width: isTablet ? '100%' : '390px' }}>
        <TextInput
          fullWidth={fullWidth}
          inputPlaceholder=""
          disabled
          inputUpperLabel={`${t('wallet-address-input-label.v')} ${label}`}
          value={handleShowWalletAddress(activeWallet?.address || '', {
            isLMobile,
            isMMobile,
            isSMobile,
            isSTablet,
          })}
          fontSize={12}
          endIcon={
            <div className={classes.iconsWrapper}>
              {withLink && (
                <IconButton
                  disabled={!activeWallet?.address}
                  size="small"
                  component={Link}
                  href={
                    activeWallet?.address ? getPolygonscanAddressUrl(activeWallet?.address) : '#'
                  }
                  target="_blank"
                >
                  <LinkIcon className={classes.copyIcon} />
                </IconButton>
              )}
              <IconButton size="small" disabled={!activeWallet?.address} onClick={handleCopy}>
                <ContentCopyIcon className={classes.copyIcon} />
              </IconButton>
            </div>
          }
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            size="small"
            variant="text"
            sx={{ textTransform: 'none' }}
            onClick={handleUpdateClick}
          >
            <HelperWrapper t={'user-wallet-add-button-label'} />
          </Button>
          <Button
            onClick={() => setIsModalOpen(true)}
            size="small"
            variant="text"
            sx={{ textTransform: 'none' }}
          >
            <HelperWrapper t={'user-wallet-details-button-label'} />
          </Button>
        </Box>
      </Box>
      <UserWalletsDetailsModal
        open={isModalOpen}
        handleClose={handleClose}
        wallets={values.wallets}
        onDefaultWalletChange={handleDefaultWalletChange}
      />
    </>
  )
}
